import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import {
  createRouter,
  createWebHashHistory,
  createWebHistory
} from 'vue-router'

import LayoutDefault from '@/views/layouts/LayoutDefault.vue'

import { useAuthStore } from './stores/authStore'
import { useUserStore } from './stores/userStore'
import { isElectron } from './utils/envUtil'

const isFileProtocol = window.location.protocol === 'file:'
const basePath = isElectron() ? '/' : '/'

const guardElectronAccess = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  if (isElectron()) {
    next()
  } else {
    next('/')
  }
}

const authGuard = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()

  // Wait for auth initialization if not yet completed
  if (!authStore.isInitialized) {
    console.log('authGuard: Waiting for auth initialization...')
    // Skip login dialog if we're navigating to login page
    const skipLoginDialog = to.path.includes('/login')
    await authStore.initialize(skipLoginDialog)
  }

  console.log('authGuard: isAuthenticated:', authStore.isAuthenticated)
  console.log('authGuard: currentUser:', authStore.currentUser)
  console.log(
    'authGuard: accessToken:',
    authStore.accessToken ? 'exists' : 'null'
  )
  console.log('authGuard: target path:', to.path)

  if (authStore.isAuthenticated) {
    next()
  } else {
    // 如果路径包含多个/login，直接重定向到根路径的/login
    if (to.path.includes('/login')) {
      console.log(
        'authGuard: detected multiple /login in path:',
        to.path,
        'redirecting to clean /login'
      )
      next({ path: '/login', replace: true })
    } else if (to.path === '/test-login') {
      next()
    } else {
      // 使用 router.replace 而不是 next() 来避免路径累积
      // 将当前路径作为查询参数传递给登录页面
      console.log('authGuard: redirecting to /login from', to.path)
      next({
        path: '/login',
        query: { redirect: to.fullPath },
        replace: true
      })
    }
  }
}

const router = createRouter({
  history: isFileProtocol
    ? createWebHashHistory()
    : // Base path must be specified to ensure correct relative paths
      // Example: For URL 'http://localhost:7801/ComfyBackendDirect',
      // we need this base path or assets will incorrectly resolve from 'http://localhost:7801/'
      createWebHistory(basePath),
  routes: [
    {
      path: '/login',
      name: 'LoginView',
      component: () => import('@/views/LoginView.vue')
    },
    {
      path: '/license-expired',
      name: 'LicenseExpiredView',
      component: () => import('@/views/LicenseExpiredView.vue')
    },
    // {
    //   path: '/test-login',
    //   name: 'TestLoginView',
    //   component: () => import('@/views/TestLoginView.vue')
    // },
    {
      path: '/',
      redirect: '/inspiration'
    },
    {
      path: '/inspiration',
      name: 'InspirationView',
      component: () => import('@/views/HomeView.vue')
    },
    {
      path: '/app',
      name: 'WorkflowAppRedirectView',
      component: () => import('@/views/WorkflowAppRedirectView.vue')
    },
    {
      path: '/creation-v2',
      name: 'CreationV2View',
      component: () => import('@/views/CreationV2View.vue')
    },
    {
      path: '/video-editor',
      name: 'VideoEditorView',
      component: () => import('@/views/VideoEditorView.vue')
    },
    {
      path: '/app/:id',
      name: 'WorkflowAppRunView',
      component: () => import('@/views/WorkflowAppRunView.vue')
    },
    {
      path: '/chat',
      name: 'ChatView',
      component: () => import('@/views/ChatView.vue')
    },
    {
      path: '/inspiration/:id',
      name: 'InspirationDetailView',
      redirect: (to) => {
        return {
          path: '/inspiration',
          query: { id: to.params.id }
        }
      }
    },
    {
      path: '/workflow-list',
      name: 'WorkflowListView',
      component: () => import('@/views/HomeView.vue')
    },
    {
      path: '/tutorials',
      name: 'TutorialListView',
      component: () => import('@/views/HomeView.vue')
    },
    {
      path: '/tutorials/:id',
      name: 'TutorialDetailView',
      component: () => import('@/views/TutorialDetailView.vue')
    },
    {
      path: '/personal-center',
      name: 'PersonalCenterView',
      component: () => import('@/views/HomeView.vue')
    },
    {
      path: '/management',
      name: 'ManagementView',
      component: () => import('@/views/ManagementView.vue')
    },
    {
      path: '/admin',
      name: 'AdminView',
      component: () => import('@/views/AdminView.vue')
    },
    {
      path: '/works-management',
      name: 'WorksManagementView',
      component: () => import('@/views/HomeView.vue')
    },
    {
      path: '/debug',
      name: 'DebugView',
      component: () => import('@/views/HomeView.vue')
    },
    {
      path: '/recharge',
      name: 'RechargePage',
      component: () => import('@/views/RechargePage.vue'),
      beforeEnter: authGuard
    },
    {
      path: '/order-history',
      name: 'OrderHistoryPage',
      component: () => import('@/views/OrderHistoryPage.vue'),
      beforeEnter: authGuard
    },
    {
      path: '/workflow',
      component: LayoutDefault,
      beforeEnter: authGuard,
      children: [
        {
          path: '',
          name: 'GraphView',
          component: () => import('@/views/GraphView.vue'),
          beforeEnter: async (_to, _from, next) => {
            const userStore = useUserStore()
            await userStore.initialize()
            if (userStore.needsLogin) {
              next('/user-select')
            } else {
              next()
            }
          }
        },
        {
          path: 'user-select',
          name: 'UserSelectView',
          component: () => import('@/views/UserSelectView.vue')
        },
        {
          path: 'server-start',
          name: 'ServerStartView',
          component: () => import('@/views/ServerStartView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'install',
          name: 'InstallView',
          component: () => import('@/views/InstallView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'welcome',
          name: 'WelcomeView',
          component: () => import('@/views/WelcomeView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'not-supported',
          name: 'NotSupportedView',
          component: () => import('@/views/NotSupportedView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'download-git',
          name: 'DownloadGitView',
          component: () => import('@/views/DownloadGitView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'manual-configuration',
          name: 'ManualConfigurationView',
          component: () => import('@/views/ManualConfigurationView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: '/metrics-consent',
          name: 'MetricsConsentView',
          component: () => import('@/views/MetricsConsentView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'desktop-start',
          name: 'DesktopStartView',
          component: () => import('@/views/DesktopStartView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'maintenance',
          name: 'MaintenanceView',
          component: () => import('@/views/MaintenanceView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'desktop-update',
          name: 'DesktopUpdateView',
          component: () => import('@/views/DesktopUpdateView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'desktop-dialog/:dialogId',
          name: 'DesktopDialogView',
          component: () => import('@/views/DesktopDialogView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'server-status',
          name: 'ServerStatus',
          component: () => import('@/views/ServerStatus.vue')
        }
      ]
    },
    {
      path: '/workflow2',
      component: LayoutDefault,
      beforeEnter: authGuard,
      children: [
        {
          path: '',
          name: 'GraphView2',
          component: () => import('@/views/GraphView.vue'),
          beforeEnter: async (_to, _from, next) => {
            const userStore = useUserStore()
            await userStore.initialize()
            if (userStore.needsLogin) {
              next('/workflow2/user-select')
            } else {
              next()
            }
          }
        },
        {
          path: 'user-select',
          name: 'UserSelectView2',
          component: () => import('@/views/UserSelectView.vue')
        },
        {
          path: 'server-start',
          name: 'ServerStartView2',
          component: () => import('@/views/ServerStartView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'install',
          name: 'InstallView2',
          component: () => import('@/views/InstallView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'welcome',
          name: 'WelcomeView2',
          component: () => import('@/views/WelcomeView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'not-supported',
          name: 'NotSupportedView2',
          component: () => import('@/views/NotSupportedView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'download-git',
          name: 'DownloadGitView2',
          component: () => import('@/views/DownloadGitView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'manual-configuration',
          name: 'ManualConfigurationView2',
          component: () => import('@/views/ManualConfigurationView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: '/metrics-consent',
          name: 'MetricsConsentView2',
          component: () => import('@/views/MetricsConsentView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'desktop-start',
          name: 'DesktopStartView2',
          component: () => import('@/views/DesktopStartView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'maintenance',
          name: 'MaintenanceView2',
          component: () => import('@/views/MaintenanceView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'desktop-update',
          name: 'DesktopUpdateView2',
          component: () => import('@/views/DesktopUpdateView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'desktop-dialog/:dialogId',
          name: 'DesktopDialogView2',
          component: () => import('@/views/DesktopDialogView.vue'),
          beforeEnter: guardElectronAccess
        },
        {
          path: 'server-status',
          name: 'ServerStatus2',
          component: () => import('@/views/ServerStatus.vue')
        }
      ]
    }
  ],

  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// 许可证过期检查：若已过期且非到期页，则重定向
router.beforeEach(async (to) => {
  if (to.path === '/license-expired') return
  try {
    const { getLicenseStatus } = await import('@/services/licenseStatusService')
    const { expired } = await getLicenseStatus()
    if (expired) {
      return '/license-expired'
    }
  } catch {
    // 网络错误等忽略，由具体接口的 503 处理
  }
})

export default router
